import React, { useEffect, useRef, useState } from "react";
import { useCashflow } from "../cashflowContext";
import type {
  MenuListProps,
  MenuProps,
  ValueContainerProps } from
"react-select";
import Select, { components } from "react-select";
import { useLanguage } from "~/hooks/useLanguage";
import type { Language } from "~/utils/language";
import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { chartsStrings } from "~/translations/dashboard/charts";
import { type monthOption } from "../chartDataFunctions";
import {
  TextField,
  reactSelectStyles,
  Button,
  IconButton } from
"~/components/ui";
import IconCloseX from "~/components/icons/IconCloseX";
import {
  DayPicker,
  type DateRange as DayPickerDateRange } from
"react-day-picker";
import { he } from "react-day-picker/locale";
import dayPickerCss from "react-day-picker/style.css";
import type { LinksFunction } from "@remix-run/server-runtime";
import moment from "moment";

export const links: LinksFunction = () => [
{ rel: "stylesheet", href: dayPickerCss }];


export interface DateRange {
  startDate: Date;
  endDate: Date;
}

type Props = {
  sendDateRange: (dateRange: DateRange) => void;
  options: monthOption[];
};

let activeMenuCleanup: (() => void) | null = null;

function pinMenuToControl(menu: HTMLDivElement | null) {
  activeMenuCleanup?.();
  activeMenuCleanup = null;
  if (!menu) return;
  const apply = () => {
    const control = menu.parentElement?.querySelector(
      ".cashflow-select-control"
    );
    if (!(control instanceof HTMLElement)) return;
    const rect = control.getBoundingClientRect();
    menu.style.position = "fixed";
    menu.style.left = `${rect.left}px`;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.right = "auto";
    menu.style.margin = "0";
    menu.style.width = `${rect.width}px`;
    menu.style.minWidth = `${rect.width}px`;
    menu.style.maxWidth = `${rect.width}px`;
    menu.style.transform = "none";
    menu.style.boxSizing = "border-box";
  };
  apply();
  const frame = requestAnimationFrame(apply);
  window.addEventListener("scroll", apply, true);
  window.addEventListener("resize", apply);
  activeMenuCleanup = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener("scroll", apply, true);
    window.removeEventListener("resize", apply);
  };
}

function setReactRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);else
  if (ref) ref.current = value;
}

const Menu = (props: MenuProps) =>
<components.Menu
  {...props}
  innerRef={(node) => {
    setReactRef(props.innerRef, node);
    pinMenuToControl(node);
  }} />;



const MenuList = (props: MenuListProps) =>
<components.MenuList
  {...props}
  innerRef={(node) => {
    setReactRef(props.innerRef, node);
    if (!node) return;
    node.style.direction = "rtl";
    node.style.overflowX = "clip";
    node.style.textAlign = "right";
    if (node.scrollLeft !== 0) node.scrollLeft = 0;
  }} />;



const CashflowTimeSelection = ({ sendDateRange, options }: Props) => {
  const { lang } = useLanguage();
  const cashflowState = useCashflow();
  const [dateRange, setDateRange] = useState<DateRange>(
    () =>
    cashflowState.transactionDateRange ?? {
      startDate: new Date(),
      endDate: new Date()
    }
  );
  const [selectedOption, setValue] = useState<monthOption>();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarRange, setCalendarRange] = useState<
    DayPickerDateRange | undefined>(
  );
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const cycleDay = cashflowState.cycleDay ?? 0;
  const skipInitialCycleReset = useRef(true);

  useEffect(() => {
    if (options.length < 3) return;
    if (window.localStorage.getItem("transactionDateRange") !== null) {
      let savedDateRange = JSON.parse(
        window.localStorage.getItem("transactionDateRange") || ""
      );
      let startDate = new Date(savedDateRange.startDate);
      let endDate = new Date(savedDateRange.endDate);
      setDateRange({ startDate: startDate, endDate: endDate });
      setValue({ label: "Custom Date", value: startDate });
      sendDateRange({ startDate: startDate, endDate: endDate });
      return;
    }
    const defaultOption = getDefaultMonthOption(options, lang);
    setValue(defaultOption);
    let dateRange = getDateRangeFromSelection(lang, defaultOption, cycleDay);
    setDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
    sendDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // The mount effect above already applies the saved or default range.
    // Running this on mount overwrites a saved custom range with "this month".
    if (skipInitialCycleReset.current) {
      skipInitialCycleReset.current = false;
      return;
    }
    setAndSetValue(selectedOption || getDefaultMonthOption(options, lang));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleDay]);

  const setAndSetValue = (option: monthOption) => {
    if (option.value) {
      setValue(option);
      let dateRange = getDateRangeFromSelection(lang, option, cycleDay);
      setDateRange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      sendDateRange({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      console.log(cycleDay);
      setIsSelectOpen(false);
    }
  };

  const ValueContainer = ({
    children,
    ...props
  }: ValueContainerProps<monthOption>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let [values, input] = (children as any);
    let text = createLabelFromDateRange(
      dateRange.startDate,
      dateRange.endDate,
      lang
    );
    return (
      <components.ValueContainer {...props}>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex max-w-full items-center pl-8 pr-3">
          <span className="truncate">{text}</span>
        </div>
        {input}
      </components.ValueContainer>);

  };

  const Option = (props: any) => {
    if (props.label === "Custom Date") {
      const rangeText = createLabelFromDateRange(
        dateRange.startDate,
        dateRange.endDate,
        lang
      );
      return (
        <components.Option {...props}>
          <div
            dir="rtl"
            className="flex w-full min-w-0 cursor-pointer items-center justify-start gap-2"
            onClick={(e) => {
              e.stopPropagation();
              setCalendarRange({
                from: dateRange.startDate,
                to: dateRange.endDate
              });
              setFromInput(moment(dateRange.startDate).format("DD.MM.YY"));
              setToInput(moment(dateRange.endDate).format("DD.MM.YY"));
              setIsCalendarOpen(true);
              setIsSelectOpen(false);
            }}>

            <span>📅</span>
            <span className="min-w-0 truncate font-medium">
              {rangeText || chartsStrings[lang].selectDates}
            </span>
          </div>
        </components.Option>);

    }
    return (
      <components.Option {...props}>
        <div
          dir="rtl"
          className="flex w-full min-w-0 items-center justify-start">

          <label className="min-w-0 truncate">{props.label}</label>
        </div>
      </components.Option>);

  };

  const handleCalendarConfirm = () => {
    if (calendarRange?.from && calendarRange?.to) {
      const start = convertLocalToUTCDate(calendarRange.from);
      const end = convertLocalToUTCDate(calendarRange.to);
      setValue({ label: "Custom Date", value: start });
      setDateRange({ startDate: start, endDate: end });
      sendDateRange({ startDate: start, endDate: end });
    }
    setIsCalendarOpen(false);
  };

  return (
    <React.Fragment>
      <div id="select-time" className="relative w-[180px] mobile:w-[180px]">
        <Select
          openMenuOnClick={true}
          isRtl
          menuShouldScrollIntoView={false}
          //closeMenuOnScroll={true}
          onMenuOpen={() => {
            setTimeout(() => {
              console.log("open");
              setIsSelectOpen(!isSelectOpen);
            }, 10);
          }}
          onMenuClose={() => {
            console.log("close");
            setIsSelectOpen(false);
          }}
          menuIsOpen={isSelectOpen}
          closeMenuOnSelect={true}
          className="w-full min-w-0 text-sm"
          classNames={{
            control: () =>
            "cashflow-select-control h-[40px] min-h-[40px] w-full min-w-0 mobile:h-[32px] mobile:min-h-[32px] !p-0 !rounded-lg",
            input: () =>
            "!absolute !m-0 !h-px !w-px !overflow-hidden !p-0 !opacity-0",
            valueContainer: () =>
            "!static h-[40px] w-full min-w-0 mobile:h-[32px] mobile:min-h-[32px]",
            container: () => "relative w-full min-w-0",
            menu: () => "!z-20",
            menuList: () => "!max-h-[450px] !max-w-full",
            option: () => "!w-full !max-w-full"
          }}
          styles={{
            ...reactSelectStyles,
            valueContainer: (base) => ({
              ...base,
              position: "static",
              padding: 0
            }),
            input: (base) => ({
              ...base,
              position: "absolute",
              width: 1,
              height: 1,
              margin: 0,
              padding: 0,
              opacity: 0,
              overflow: "hidden"
            }),
            menu: (base) => ({
              ...reactSelectStyles.menu(base),
              margin: 0,
              boxSizing: "border-box"
            }),
            menuList: (base) => ({
              ...base,
              direction: "rtl",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflowX: "clip",
              overflowY: "auto",
              textAlign: "right",
              WebkitOverflowScrolling: "auto"
            }),
            option: (base, state) => ({
              ...reactSelectStyles.option(base, state),
              direction: "rtl",
              textAlign: "right",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box"
            })
          }}
          hideSelectedOptions={false}
          options={options}
          isSearchable={false}
          onChange={(option) => {
            if (option?.label === "Custom Date") {
              setCalendarRange({
                from: dateRange.startDate,
                to: dateRange.endDate
              });
              setFromInput(moment(dateRange.startDate).format("DD.MM.YY"));
              setToInput(moment(dateRange.endDate).format("DD.MM.YY"));
              setIsCalendarOpen(true);
              setIsSelectOpen(false);
              return;
            }
            option ? setAndSetValue(option) : console.log("no option"); //setValue(options[0]);
          }}
          components={{
            ValueContainer,
            Option,
            Menu,
            MenuList
          }}
          value={
          selectedOption?.label ?
          options.filter(function (option) {
            return option.label === selectedOption?.label;
          }) :
          {
            label: chartsStrings[lang].noPeriodSelected,
            value: new Date()
          }}>

        </Select>
      </div>

      {/* Calendar Modal */}
      {isCalendarOpen &&
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        onClick={() => setIsCalendarOpen(false)}>

          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" />
          {/* Modal */}
          <div
          className="relative z-10 min-w-[320px] rounded-xl bg-surface p-5 shadow-2xl"
          dir={lang === "HEB" ? "rtl" : "ltr"}
          onClick={(e) => e.stopPropagation()}>

            {/* Header with X */}
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-700">
                {chartsStrings[lang].selectDateRange}
              </h3>
              <IconButton
              onClick={() => setIsCalendarOpen(false)}
              icon={<IconCloseX fillColor="currentColor" />}
              variant="ghost"
              size="sm"
              aria-label={chartsStrings[lang].close} />

            </div>

            {/* Editable date inputs */}
            <div className="mb-3 flex items-center justify-center gap-3 text-sm">
              <div className="flex-1 text-center">
                <div className="mb-1 text-xs text-gray-400">
                  {chartsStrings[lang].fromDate}
                </div>
                <TextField
                type="text"
                inputMode="numeric"
                className="text-center text-sm font-medium"
                placeholder="DD.MM.YY"
                value={fromInput}
                onChange={(value) => setFromInput(value)}
                onBlur={() => {
                  const parsed = parseDateInput(fromInput);
                  if (parsed) {
                    setCalendarRange((prev) => ({
                      from: parsed,
                      to: prev?.to
                    }));
                    setFromInput(moment(parsed).format("DD.MM.YY"));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const parsed = parseDateInput(fromInput);
                    if (parsed) {
                      setCalendarRange((prev) => ({
                        from: parsed,
                        to: prev?.to
                      }));
                      setFromInput(moment(parsed).format("DD.MM.YY"));
                    }
                  }
                }} />

              </div>
              <div className="mt-4 text-gray-300">←</div>
              <div className="flex-1 text-center">
                <div className="mb-1 text-xs text-gray-400">
                  {chartsStrings[lang].toDate}
                </div>
                <TextField
                type="text"
                inputMode="numeric"
                className="text-center text-sm font-medium"
                placeholder="DD.MM.YY"
                value={toInput}
                onChange={(value) => setToInput(value)}
                onBlur={() => {
                  const parsed = parseDateInput(toInput);
                  if (parsed) {
                    setCalendarRange((prev) => ({
                      from: prev?.from,
                      to: parsed
                    }));
                    setToInput(moment(parsed).format("DD.MM.YY"));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const parsed = parseDateInput(toInput);
                    if (parsed) {
                      setCalendarRange((prev) => ({
                        from: prev?.from,
                        to: parsed
                      }));
                      setToInput(moment(parsed).format("DD.MM.YY"));
                    }
                  }
                }} />

              </div>
            </div>

            {/* Calendar with month/year dropdowns */}
            <DayPicker
            mode="range"
            locale={lang === "HEB" ? he : undefined}
            dir={lang === "HEB" ? "rtl" : "ltr"}
            selected={calendarRange}
            onSelect={(range) => {
              setCalendarRange(range);
              if (range?.from)
              setFromInput(moment(range.from).format("DD.MM.YY"));
              if (range?.to) setToInput(moment(range.to).format("DD.MM.YY"));else
              setToInput("");
            }}
            disabled={{ after: new Date() }}
            numberOfMonths={1}
            showOutsideDays
            captionLayout="dropdown"
            startMonth={new Date(2015, 0)}
            endMonth={new Date()}
            styles={{
              root: { fontSize: "1rem" }
            }} />


            {/* Confirm button */}
            <Button
            onClick={handleCalendarConfirm}
            disabled={!calendarRange?.from || !calendarRange?.to}
            fullWidth
            className="mt-3">

              {chartsStrings[lang].confirm}
            </Button>
          </div>
        </div>}

    </React.Fragment>);

};

export default CashflowTimeSelection;

/** Default to the current month if it exists in options, otherwise the first real month (options[2]). */
function getDefaultMonthOption(
options: monthOption[],
lang: Language)
: monthOption {
  if (options.length < 3) return options[options.length - 1] ?? options[0];
  const now = new Date();
  const currentMonthOption = options.find(
    (o) =>
    o.value &&
    o.label !== "Custom Date" &&
    o.label !== cashflowCharts[lang].Months.All &&
    o.value.getFullYear() === now.getFullYear() &&
    o.value.getMonth() === now.getMonth()
  );
  return currentMonthOption ?? options[2];
}

function getDateRangeFromSelection(
lang: Language,
selectedMonth: monthOption,
cycleDay: number)
: DateRange {
  let start = addDays(selectedMonth.value, cycleDay);
  let end = addMonth(start);
  if (selectedMonth.label === cashflowCharts[lang].Months.All) {
    //assign now to end date
    end = addMonth(new Date());
  }
  return { startDate: start, endDate: end };
}

function addDays(date: Date, days: number) {
  const newDate = new Date(date.getTime() + days * 24 * 60 * 60 * 1000);

  return newDate;
}

function addMonth(date: Date): Date {
  const result = new Date(date); // Clone the original date to avoid modifying it
  //reduce one day from the date
  result.setMonth(result.getMonth() + 1); // Add one month
  result.setDate(result.getDate() - 1);

  return result;
}

export function readStoredCashflowDateRange(): DateRange | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = window.localStorage.getItem("transactionDateRange");
  if (!raw) return undefined;
  try {
    const saved = JSON.parse(raw);
    const startDate = new Date(saved.startDate);
    const endDate = new Date(saved.endDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return undefined;
    }
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  } catch {
    return undefined;
  }
}

/** Saved range, or the current month once transactions can name one. */
export function resolveCashflowDateRange(
lang: Language,
months: monthOption[],
cycleDay: number)
: DateRange | undefined {
  const stored = readStoredCashflowDateRange();
  if (stored) return stored;
  if (months.length < 3) return undefined;
  const option = getDefaultMonthOption(months, lang);
  if (!option?.value || Number.isNaN(option.value.getTime())) return undefined;
  const range = getDateRangeFromSelection(lang, option, cycleDay);
  range.endDate.setHours(23, 59, 59, 999);
  return range;
}

export function createLabelFromDateRange(
startDate?: Date,
endDate?: Date,
lang?: Language)
: string {
  if (!startDate || !endDate) {
    return "";
  }
  return `${toNumberedDate(startDate)} ${
  chartsStrings[lang || "HEB"].until
  } ${toNumberedDate(endDate)}`;
}

// function toFormatedDate(date: Date): string {
//     const dateObj = new Date(date);
//     const monthStr = translateMonth(dateObj.getUTCMonth())
//     const day = dateObj.getUTCDate();
//     return `${day}-${monthStr}`;
// }

function toNumberedDate(date: Date): string {
  return moment(date).format("DD.MM");
}

function convertLocalToUTCDate(date: Date) {
  if (!date) {
    return date;
  }
  date = new Date(date);
  date = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  return date;
}

/** Parse user-typed date strings like "DD.MM.YY", "DD.MM.YYYY", "DD/MM/YY", "DD-MM-YY" */
function parseDateInput(input: string): Date | null {
  const cleaned = input.trim().replace(/[/-]/g, ".");
  const parts = cleaned.split(".");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  let year = parseInt(parts[2], 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  if (year < 100) year += 2000;
  const date = new Date(year, month, day);
  if (isNaN(date.getTime())) return null;
  if (date.getDate() !== day || date.getMonth() !== month) return null; // invalid day/month overflow
  if (date > new Date()) return null; // don't allow future
  return date;
}